import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './PaymentSuccess.module.css';
import { LanguageContext } from '../../context/LanguageContext';
import { scrollToTop } from '../../utils/helpers/utils';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { PLAN_ORDER } from '../../utils/helpers/subscriptions/plans';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [searchParams] = useSearchParams();
  const { setUser } = useCurrentUser();

  useEffect(() => {
    const plan = searchParams.get('plan');
    if (plan && PLAN_ORDER.includes(plan)) {
      setUser((prev) => ({ ...prev, plan }));
    }
  }, [setUser, searchParams]);

  useEffect(() => {
    sessionStorage.removeItem("selectedPlan");
    scrollToTop()

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = Array.from({ length: 80 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height - canvas.height,
      size: Math.random() * 8 + 4,
      speed: Math.random() * 3 + 1.5,
      wobble: Math.random() * Math.PI * 2,
      wobbleSpeed: Math.random() * 0.05 + 0.02,
      color: ['#09BCC1', '#f5a623', '#3dd9bc', '#f09010', '#6ee7df', '#ffd280'][Math.floor(Math.random() * 6)],
      shape: Math.random() > 0.5 ? 'rect' : 'circle',
      rotation: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.1,
    }));

    let animId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.y += p.speed;
        p.wobble += p.wobbleSpeed;
        p.x += Math.sin(p.wobble) * 1.5;
        p.rotation += p.rotSpeed;
        if (p.y > canvas.height + 20) {
          p.y = -20;
          p.x = Math.random() * canvas.width;
        }
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.85;
        if (p.shape === 'rect') {
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.5);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      });
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <LanguageContext.Consumer>
      {({ data, language: lang }) => {
        const t = (key) => data?.[key]?.[lang];

        return (
          <div className={styles.page}>
            <canvas ref={canvasRef} className={styles.canvas} />

            <div className={styles.card}>
              <div className={styles.iconRing}>
                <svg viewBox="0 0 52 52" className={styles.checkSvg}>
                  <circle cx="26" cy="26" r="25" fill="none" className={styles.checkCircle} />
                  <path fill="none" className={styles.checkPath} d="M14 27l8 8 16-16" />
                </svg>
              </div>

              <h1 className={styles.title}>{t("payment_successful")}</h1>
              <p className={styles.subtitle}>{t("subscription_active_message")}</p>

              <div className={styles.divider} />

              <div className={styles.actions}>
                <button className={styles.primaryBtn} onClick={() => navigate('/')}>
                  {t("start_exploring")}
                </button>
                <button className={styles.secondaryBtn} onClick={() => navigate('/subscription')}>
                  {t("view_my_plan")}
                </button>
              </div>
            </div>
          </div>
        );
      }}
    </LanguageContext.Consumer>
  )
};

export default PaymentSuccess;